import { AuthenticationType } from '@portal-front-end/models';
import { AuthenticationApiService } from '@portal-front-end/api';
import { environment } from './../../environments/environment';
import { catchError, take, concatMap, mergeMap, concatAll, retry } from 'rxjs/operators';
import { AuthCookieService } from './auth-cookie.service';
import {
    HttpEvent,
    HttpHandler,
    HttpInterceptor,
    HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, Subject, of } from 'rxjs';
import { AuthStore } from '.';
import { ofType } from '@ngrx/effects';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class AuthStoreInterceptor implements HttpInterceptor {
    constructor(
        private cookie: AuthCookieService, 
        private authApi: AuthenticationApiService, 
        private authStore: AuthStore,
        private matSnakbar: MatSnackBar    
    ) { }

    AUTHENTICATION_URL = environment.authenticationServer;
    private isRefreshing = false;
    private refreshed$ = new Subject();

    intercept(
        req: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        const hasExistingSession = this.cookie.hasSavedSession();
        let refreshToken;
        // if access token is present on local storage, include on every request
        if (hasExistingSession && !req.url.includes(this.AUTHENTICATION_URL)) {
            refreshToken = this.cookie.refreshToken;
            req = req.clone({
                setHeaders: {
                    Authorization: `Bearer ${this.cookie.accessToken}`
                }
            });
        } else {
            req = req.clone({
                setHeaders: {
                    Authorization: `Basic ${btoa(environment.clientCredentials)}`
                }
            });
        }

        if (!req.url.includes(this.AUTHENTICATION_URL) && this.isRefreshing) {
            return this.refreshed$.pipe(
                take(1),
                concatMap(() => next.handle(req))
            );
        }

        return next.handle(req).pipe(
            catchError(err => {
                if (err.status && err.status === 401) {

                    if (req.url.includes(this.AUTHENTICATION_URL)) {
                        return throwError(err);
                    }
                    // Start queueing requests until refresh is completed
                    this.isRefreshing = true;

                    return this.authApi.refreshAccessToken(refreshToken).pipe(
                        concatMap((authType: AuthenticationType) => {
                            // Release queued requests
                            this.isRefreshing = false;
                            this.refreshed$.next();
                            this.cookie.storeAuth(authType);
                            this.cookie.storeAccessToken(authType.access_token);
                            req = req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${authType.access_token}`
                                }
                            });
                            return next.handle(req);
                        }),
                        catchError(rErr => {
                            // If refresh_token token is expired, ask user to refresh it
                            this.isRefreshing = false;
                            if (rErr.status && (rErr.status === 401 || rErr.status === 404)) {
                                return (
                                    this.authStore
                                        .logout()
                                        // logout the app, return empty event
                                        // to satisfy types
                                        .pipe(mergeMap(() => of<HttpEvent<any>>()))
                                );
                            }
                            return throwError(err);
                        })
                    );
                } 
                // might be better to do explicit error handling than this
                // otherwise there will be double ups when doing explicit error handling
                /* else if(err.status && err.status === 400){
                    // TODO - may be do dialog boxes instead of these bars
                    this.matSnakbar.open(err.error.message,'close',{
                        duration: 20000,
                        horizontalPosition: 'end'
                    });
                } else if(err.status && +err.status >= 500){
                    this.matSnakbar.open("Something went wrong",'close',{
                        duration: 10000,
                        horizontalPosition: 'end'
                    });
                } */

                return throwError(err);
            })
        );
    }
}
